import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Stack,
  Divider,
  LinearProgress,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Schedule,
  CheckCircle,
  Cancel,
  Person,
  AccessTime,
  BarChart,
  PieChart,
  Refresh,
  CalendarToday,
  AttachMoney,
  Percent,
  ArrowUpward,
  ArrowDownward,
  Remove,
  Analytics,
  People,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAppointmentAnalytics } from '../features/appointments/appointmentSearchSlice';
import { fetchAppointmentStats } from '../features/appointments/appointmentsSlice';
import { fetchDashboardStats, fetchDashboardTrends, fetchConsultationDistribution } from '../features/doctors/dashboardSlice';
import axiosInstance from '../api/axiosInstance';
import { format, subDays, parseISO, isAfter, isBefore } from 'date-fns';
import { motion } from 'framer-motion';

const AppointmentAnalytics = ({ userRole = 'doctor', userId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { analytics: clientAnalytics, loading: clientLoading, error: rawError } = useSelector(state => state.appointmentSearch);
  const { stats } = useSelector(state => state.appointments);
  
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(false);
  
  // Redux state for backend analytics
  const { 
    trends: trendsState, 
    consultationDistribution: consultationState,
    stats: dashboardStatsState 
  } = useSelector(state => state.doctorDashboard);
  
  // Normalize error to always be a string or null
  const error = useMemo(() => {
    if (!rawError) return null;
    if (typeof rawError === 'string') return rawError;
    
    if (rawError?.detail) {
      const detail = rawError.detail;
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object') {
        if (detail?.message) return String(detail.message);
        if (detail?.error) return String(detail.error);
        return 'An error occurred';
      }
    }
    
    if (rawError?.message) {
      const message = rawError.message;
      if (typeof message === 'string') return message;
      if (typeof message === 'object') return 'An error occurred';
      return String(message);
    }
    
    return 'Failed to load analytics';
  }, [rawError]);

  // Load all analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch client-side analytics (for date filtering)
      await dispatch(fetchAppointmentAnalytics({
        user_id: userId || user?.id,
        user_role: userRole,
        period_days: parseInt(timeRange)
      })).unwrap();
      
      // Fetch backend analytics endpoints (overall data)
      const months = timeRange === '7' ? 2 : timeRange === '30' ? 3 : timeRange === '90' ? 6 : 12;
      
      if (userRole === 'doctor') {
        await Promise.all([
          dispatch(fetchDashboardTrends(months)).unwrap(),
          dispatch(fetchConsultationDistribution()).unwrap(),
          dispatch(fetchDashboardStats()).unwrap(),
          dispatch(fetchAppointmentStats({})).unwrap(), // Overall stats
        ]);
      } else {
        await dispatch(fetchAppointmentStats({})).unwrap();
      }
      
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, userRole, userId, user?.id, dispatch]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Extract data from Redux state
  const revenueTrends = trendsState?.data || null;
  const consultationDistribution = consultationState?.data || null;
  const dashboardStats = dashboardStatsState?.data || null;
  
  // Calculate trends and comparisons
  const calculateTrends = useMemo(() => {
    if (!revenueTrends || !Array.isArray(revenueTrends) || revenueTrends.length < 2) return null;
    
    // Reverse to get chronological order (oldest to newest)
    const trends = [...revenueTrends].reverse();
    const current = trends[trends.length - 1];
    const previous = trends[trends.length - 2];
    
    return {
      revenue: {
        current: current.revenue || 0,
        previous: previous.revenue || 0,
        change: current.revenue && previous.revenue 
          ? ((current.revenue - previous.revenue) / previous.revenue * 100).toFixed(1)
          : '0.0',
        direction: current.revenue > previous.revenue ? 'up' : current.revenue < previous.revenue ? 'down' : 'stable'
      },
      newPatients: {
        current: current.new_patients || 0,
        previous: previous.new_patients || 0,
        change: current.new_patients && previous.new_patients
          ? ((current.new_patients - previous.new_patients) / previous.new_patients * 100).toFixed(1)
          : '0.0',
        direction: current.new_patients > previous.new_patients ? 'up' : current.new_patients < previous.new_patients ? 'down' : 'stable'
      },
      followUps: {
        current: current.follow_ups || 0,
        previous: previous.follow_ups || 0,
        change: current.follow_ups && previous.follow_ups
          ? ((current.follow_ups - previous.follow_ups) / previous.follow_ups * 100).toFixed(1)
          : '0.0',
        direction: current.follow_ups > previous.follow_ups ? 'up' : current.follow_ups < previous.follow_ups ? 'down' : 'stable'
      }
    };
  }, [revenueTrends]);

  const getStatusColor = (status) => {
    const colors = {
      'Completed': theme.palette.success.main,
      'Confirmed': theme.palette.info.main,
      'Pending': theme.palette.warning.main,
      'Cancelled': theme.palette.error.main,
      'Canceled': theme.palette.error.main,
      'No Show': theme.palette.grey[500]
    };
    return colors[status] || theme.palette.grey[500];
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Completed': <CheckCircle />,
      'Confirmed': <Schedule />,
      'Pending': <Schedule />,
      'Cancelled': <Cancel />,
      'Canceled': <Cancel />,
      'No Show': <Cancel />
    };
    return icons[status] || <Schedule />;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value, total) => {
    if (!total || total === 0) return '0.0';
    return ((value / total) * 100).toFixed(1);
  };

  // Safely extract analytics data
  const { 
    total_appointments = 0,
    status_distribution: rawStatusDistribution = [],
    type_distribution: rawTypeDistribution = [],
    total_revenue = 0,
    appointments_over_time: rawAppointmentsOverTime = []
  } = clientAnalytics || {};
  
  // Normalize arrays
  const status_distribution = Array.isArray(rawStatusDistribution) 
    ? rawStatusDistribution.map(item => ({
        status: typeof item.status === 'object' ? 'Unknown' : String(item.status || 'Unknown'),
        count: typeof item.count === 'number' ? item.count : parseInt(item.count) || 0
      }))
    : [];

  const type_distribution = Array.isArray(rawTypeDistribution)
    ? rawTypeDistribution.map(item => ({
        type: typeof item.type === 'object' ? 'Unknown' : String(item.type || 'Unknown'),
        count: typeof item.count === 'number' ? item.count : parseInt(item.count) || 0
      }))
    : [];

  const appointments_over_time = Array.isArray(rawAppointmentsOverTime)
    ? rawAppointmentsOverTime.map(item => ({
        day: typeof item.day === 'object' ? String(item.day) : String(item.day || ''),
        count: typeof item.count === 'number' ? item.count : parseInt(item.count) || 0
      })).map(item => ({
        ...item,
        formattedDay: item.day ? format(parseISO(item.day), 'MMM dd') : item.day
      }))
    : [];

  // Status colors for pie chart
  const STATUS_COLORS = {
    'Completed': '#4caf50',
    'Confirmed': '#2196f3',
    'Pending': '#ff9800',
    'Cancelled': '#f44336',
    'Canceled': '#f44336',
    'No Show': '#9e9e9e'
  };

  const getStatusChartColor = (status) => STATUS_COLORS[status] || '#757575';

  // Consultation type colors (from backend)
  const consultationColors = consultationDistribution?.reduce((acc, item) => {
    acc[item.name] = item.color;
    return acc;
  }, {}) || {};

  const isLoading = loading || clientLoading || trendsState?.loading || consultationState?.loading || dashboardStatsState?.loading;
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading analytics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load analytics: {error}
        <Button size="small" onClick={loadAnalytics} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  // Calculate key metrics from stats
  const completedCount = stats?.by_status?.Completed || status_distribution.find(s => s.status === 'Completed')?.count || 0;
  const cancelledCount = (stats?.by_status?.Canceled || 0) + (stats?.by_status?.Cancelled || 0) || 
                        status_distribution.find(s => s.status === 'Cancelled' || s.status === 'Canceled')?.count || 0;
  const pendingCount = stats?.by_status?.Pending || status_distribution.find(s => s.status === 'Pending')?.count || 0;
  const confirmedCount = stats?.by_status?.Confirmed || status_distribution.find(s => s.status === 'Confirmed')?.count || 0;
  const totalFromStats = stats?.total || total_appointments;
  const todayCount = stats?.today || 0;

  // Calculate completion rate
  const completionRate = totalFromStats > 0 
    ? ((completedCount / totalFromStats) * 100).toFixed(1)
    : '0.0';

  // Calculate cancellation rate
  const cancellationRate = totalFromStats > 0
    ? ((cancelledCount / totalFromStats) * 100).toFixed(1)
    : '0.0';

  const TrendIndicator = ({ value, direction }) => {
    if (!value || value === '0.0' || direction === 'stable') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          <Remove fontSize="small" />
          <Typography variant="body2" color="text.secondary">No change</Typography>
        </Box>
      );
    }
    
    const isPositive = direction === 'up';
    const Icon = isPositive ? ArrowUpward : ArrowDownward;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color: isPositive ? 'success.main' : 'error.main' }}>
        <Icon fontSize="small" />
        <Typography variant="body2" color={isPositive ? 'success.main' : 'error.main'}>
          {isPositive ? '+' : ''}{value}%
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Appointment Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive insights and trends for your appointments
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAnalytics}
            disabled={loading || clientLoading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Appointments */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Total Appointments
                    </Typography>
                    <Schedule sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    {totalFromStats.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {todayCount} scheduled for today
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Completed */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Completed
                    </Typography>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {completedCount.toLocaleString('en-IN')}
                  </Typography>
                  <Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={parseFloat(completionRate)} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.success.main
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {completionRate}% completion rate
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Pending */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Pending
                    </Typography>
                    <Schedule sx={{ color: 'warning.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="warning.main">
                    {pendingCount.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {confirmedCount} confirmed
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Revenue
                    </Typography>
                    <AttachMoney sx={{ color: 'info.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="info.main">
                    {formatCurrency(
                      total_revenue || 
                      (revenueTrends && revenueTrends.length > 0 ? revenueTrends[revenueTrends.length - 1]?.revenue : 0) ||
                      0
                    )}
                  </Typography>
                  {calculateTrends && (
                    <TrendIndicator 
                      value={calculateTrends.revenue.change} 
                      direction={calculateTrends.revenue.direction} 
                    />
                  )}
                  {!calculateTrends && total_revenue === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Revenue data not available
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Appointments Over Time */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card sx={{ height: '100%', boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Appointments Over Time
                  </Typography>
                  <Chip 
                    icon={<BarChart />} 
                    label={`Last ${timeRange} days`} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ height: 350 }}>
                  {appointments_over_time.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={appointments_over_time}>
                        <defs>
                          <linearGradient id="colorAppointments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                        <XAxis 
                          dataKey="formattedDay" 
                          tick={{ fill: theme.palette.text.secondary }}
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis tick={{ fill: theme.palette.text.secondary }} />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="count" 
                          stroke={theme.palette.primary.main} 
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorAppointments)"
                          name="Appointments"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected period
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Status Distribution */}
        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card sx={{ height: '100%', boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Status Distribution
                </Typography>
                <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {status_distribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={status_distribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {status_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getStatusChartColor(entry.status)} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No status data available
                    </Typography>
                  )}
                </Box>
                {/* Status Legend */}
                <Box sx={{ mt: 2 }}>
                  <Stack spacing={1}>
                    {status_distribution.map((status) => (
                      <Box key={status.status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              backgroundColor: getStatusChartColor(status.status) 
                            }} 
                          />
                          <Typography variant="body2">{status.status}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {status.count}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Revenue Trends Chart */}
      {revenueTrends && Array.isArray(revenueTrends) && revenueTrends.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card sx={{ boxShadow: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Revenue & Patient Trends (Monthly)
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip 
                        icon={<People />} 
                        label="New Patients" 
                        size="small" 
                        sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}
                      />
                      <Chip 
                        icon={<TrendingUp />} 
                        label="Revenue" 
                        size="small" 
                        sx={{ backgroundColor: alpha(theme.palette.success.main, 0.1) }}
                      />
                    </Stack>
                  </Box>
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={[...revenueTrends].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: theme.palette.text.secondary }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fill: theme.palette.text.secondary }}
                          label={{ value: 'Patients', angle: -90, position: 'insideLeft' }}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          tick={{ fill: theme.palette.text.secondary }}
                          label={{ value: 'Revenue (₹)', angle: 90, position: 'insideRight' }}
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => {
                            if (name === 'revenue') return formatCurrency(value);
                            return value;
                          }}
                        />
                        <Legend />
                        <Bar 
                          yAxisId="left" 
                          dataKey="new_patients" 
                          fill={theme.palette.info.main}
                          name="New Patients"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar 
                          yAxisId="left" 
                          dataKey="follow_ups" 
                          fill={alpha(theme.palette.info.main, 0.6)}
                          name="Follow-ups"
                          radius={[8, 8, 0, 0]}
                        />
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke={theme.palette.success.main} 
                          strokeWidth={3}
                          dot={{ fill: theme.palette.success.main, r: 4 }}
                          name="Revenue"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  {/* Trend indicators */}
                  {calculateTrends && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Revenue Trend
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {formatCurrency(calculateTrends.revenue.current)}
                            </Typography>
                            <TrendIndicator 
                              value={calculateTrends.revenue.change} 
                              direction={calculateTrends.revenue.direction} 
                            />
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            New Patients Trend
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {calculateTrends.newPatients.current}
                            </Typography>
                            <TrendIndicator 
                              value={calculateTrends.newPatients.change} 
                              direction={calculateTrends.newPatients.direction} 
                            />
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Follow-ups Trend
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {calculateTrends.followUps.current}
                            </Typography>
                            <TrendIndicator 
                              value={calculateTrends.followUps.change} 
                              direction={calculateTrends.followUps.direction} 
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      )}

      {/* Consultation Distribution & Detailed Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Consultation Type Distribution */}
        {consultationDistribution && consultationDistribution.length > 0 && (
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Consultation Type Distribution
                  </Typography>
                  <Box sx={{ height: 300, mb: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={consultationDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {consultationDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={1}>
                    {consultationDistribution.map((item) => (
                      <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              backgroundColor: item.color 
                            }} 
                          />
                          <Typography variant="body2">{item.name}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.value}%
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}

        {/* Appointment Types List */}
        {type_distribution && type_distribution.length > 0 && (
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Appointment Types
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {type_distribution.map((type, index) => {
                      const percentage = formatPercentage(type.count, totalFromStats);
                      const color = consultationColors[type.type] || theme.palette.primary.main;
                      
                      return (
                        <Box key={index}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {type.type}
                            </Typography>
                            <Chip
                              label={`${type.count} (${percentage}%)`}
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(color, 0.1),
                                color: color,
                                fontWeight: 600
                              }}
                            />
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={parseFloat(percentage)} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: alpha(color, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: color
                              }
                            }} 
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}
      </Grid>

      {/* Detailed Status Breakdown Table */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Detailed Status Breakdown
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Percentage</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Share</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {status_distribution.map((status) => {
                        const percentage = formatPercentage(status.count, totalFromStats);
                        const statusColor = getStatusChartColor(status.status);
                        
                        return (
                          <TableRow 
                            key={status.status}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(statusColor, 0.05)
                              }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ color: statusColor }}>
                                  {getStatusIcon(status.status)}
                                </Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {status.status}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" fontWeight={600}>
                                {status.count.toLocaleString('en-IN')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${percentage}%`}
                                size="small"
                                sx={{ 
                                  backgroundColor: alpha(statusColor, 0.1),
                                  color: statusColor,
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ width: 100, display: 'inline-block' }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={parseFloat(percentage)} 
                                  sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    backgroundColor: alpha(statusColor, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: statusColor
                                    }
                                  }} 
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {status_distribution.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No status data available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppointmentAnalytics;
